import { type Dispatch, useCallback, useEffect, useRef } from 'react';

type SelectValue<T> = T | T[] | null;

type UseAutoSelectSingleOptionParams<TOption> = {
  enabled?: boolean;
  loading?: boolean;
  multiple?: boolean;
  options: TOption[];
  value: TOption | TOption[] | null | undefined;
  setValue: Dispatch<SelectValue<TOption>>;
};

export const useAutoSelectSingleOption = <TOption>({
  enabled = false,
  loading = false,
  multiple = false,
  options,
  value,
  setValue,
}: UseAutoSelectSingleOptionParams<TOption>) => {
  const isManuallyClearedRef = useRef(false);

  useEffect(() => {
    const isEmptyValue = multiple
      ? Array.isArray(value) && value.length === 0
      : value == null;

    if (
      !enabled ||
      isManuallyClearedRef.current ||
      loading ||
      options.length !== 1 ||
      !isEmptyValue
    ) {
      return;
    }

    setValue(multiple ? [options[0]] : options[0]);
  }, [enabled, loading, multiple, options, setValue, value]);

  return useCallback(
    (newValue: SelectValue<TOption>) => {
      isManuallyClearedRef.current = multiple
        ? Array.isArray(newValue) && newValue.length === 0
        : newValue == null;

      setValue(newValue);
    },
    [multiple, setValue]
  );
};
